import React, { useContext, useEffect, useState } from "react";
import {
    ButtonDiv,
    HeaderText,
    Wrapper,
    EmptyWrapper,
    EmptyWrapperSubTitle,
    EmptyWrapperTitle,
    StyledAccountsTable,
    DropdownContainer,
} from "./style";
import CustomModal from "../../common/CustomModal";
import CustomTable from "../../common/CustomTable";
import { useWindowSize } from "@reach/window-size";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import AccountCard from "./components/AccountCard";
import {
    addUpdateBankAccount,
    merchantBankAccounts,
    updateBankAccount,
} from "../../../pages/Settings/settingsSlice";
import AccountModal from "./components/AccountModal";
import { FormInputs } from "./components/types";
import { SuccessAlertContext } from "../../../context/SuccessAlertContext";
import Loader from "../../common/Loader";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { selectUserRoleAndPermissions } from "../../../pages/Settings/Team/redux/slice";
import CustomSelectTwo from "../../common/CustomSelect/CustomSelectTwo";
import AccountTypeConfirmationModal from "./components/AccountTypeConfirmationModal";
import { ErrorAlertContext } from "../../../context/ErrorAlertContext";
import { ReactComponent as PendingStatusIcon } from "../../emptyStates/Dashboard/assets/info-reviewed.svg";
import BusinessStatus from "../../emptyStates/Dashboard/components/BusinessStatus";

const EmptyBalanceDescription = () => {
    return (
        <EmptyWrapper>
            <EmptyWrapperTitle>No accounts</EmptyWrapperTitle>
            <EmptyWrapperSubTitle>
                No bank accounts added yet.
            </EmptyWrapperSubTitle>
        </EmptyWrapper>
    );
};

const Accounts = () => {
    const { width } = useWindowSize();
    const dispatch = useAppDispatch();
    const { onShowAlert } = useContext(SuccessAlertContext);
    const { onShowAlert: onErrorAlert } = useContext(ErrorAlertContext);
    const { onShowAlert: onShowErrorAlert } = useContext(
        NetworkErrorAlertContext
    );

    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState("add");
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState<any>({});
    const [hoveredRowId, setHoveredRowId] = useState(null);
    const [showConfirmAcctTypeChangeModal, setShowConfirmAcctTypeChangeModal] = useState(false);
    const [pendingChange, setPendingChange] = useState(null);

    const { role, permissions } = useAppSelector(
        selectUserRoleAndPermissions,
    );
    const { businessId, userId } = useAppSelector((state) => state.users);
    const { status, bankAccounts, businessCurrency } = useAppSelector((state) => state.settings);
    const hasPrimaryAccount = bankAccounts?.find((acc) => acc.primary)
    const hasAddBankPermission = permissions?.["bank-account"]?.includes("ADD_BANK_ACCOUNT");

    const accountTypeOptions = [
        {
            label: 'Primary',
            value: 'primary'
        }, 
        {
            label: 'Secondary',
            value: 'secondary'
        }
    ]

    const tableColumns = [
        {
            title: "Bank name",
            dataIndex: "bankName",
            key: "bankName",
        },
        {
            title: "Account number",
            dataIndex: "accNum",
            key: "accNum",
        },
        {
            title: "Account name",
            dataIndex: "accName",
            key: "accName",
        },
        {
            title: "Currency",
            dataIndex: "currency",
            key: "currency",
        },
        {
            title: "Account",
            dataIndex: "primary",
            key: "primary",
            render: (text, record) => {
                const isHovered = hoveredRowId === record.id
                const currentType = record.primary ? 'primary' : 'secondary'

                return (
                    <span>
                        {isHovered ? (
                            <DropdownContainer>
                                <CustomSelectTwo
                                    $height="33px"
                                    $fontSize="12px"
                                    value={currentType}
                                    options={accountTypeOptions}
                                    onChange={(value) => handleAccountTypeChange(record.id, value, currentType)}
                                />
                            </DropdownContainer>
                        ) : (
                            <span>{record.primary ? 'Primary' : 'Secondary'}</span>
                        )}
                    </span>
                )
            }
        },
    ];

    const handleMouseEnter = (record) => {
        setHoveredRowId(record.id);
    };

    const handleMouseLeave = () => {
        setHoveredRowId(null);
    };

    const handleAccountTypeChange = (accountId, newType, currentType) => {
        if (newType === currentType) return;

        const accountOfInterest = accounts?.find((item) => item.id === accountId)

        if (accountOfInterest?.primary) {
            onErrorAlert({title: "Primary account required", message: "A primary account must always be selected for settlement."});
            return
        }
        if (businessCurrency !== accountOfInterest?.currency) {
            onErrorAlert({title: "Currencies mismatch", message: "Add a primary account with same currency as the current primary account."});
            return
        }
        
        setPendingChange({
            accountId,
            newType,
            currentType
        });
        setShowConfirmAcctTypeChangeModal(true);
        setHoveredRowId(null);
    };

    const confirmAccountTypeChange = async (bankAccountId: string) => {
        const accountOfInterest = accounts?.find((item) => item.id === bankAccountId)

        if (pendingChange) {
            const payload = {
                ...accountOfInterest,
                primary: pendingChange.newType === 'primary' 
            }
            await dispatch(updateBankAccount({bankAccountId, payload})).unwrap()
            onShowAlert("Primary account selected successfully");
            await dispatch(merchantBankAccounts(userId))
        }
        
        setShowConfirmAcctTypeChangeModal(false);
        setPendingChange(null);
    };

    const cancelAccountTypeChange = () => {
        setShowConfirmAcctTypeChangeModal(false);
        setPendingChange(null);
    };

    const onSaveAccount = async (payload: FormInputs) => {
        const accountCode = payload.currency === "NGN" ? payload.bankCode : ""

        const addPayload = {
            bankName: payload.bankName,
            accountNumber: payload.accNum,
            accountName: payload.accName,
            swiftCode: payload.swiftCode,
            country: payload.country,
            currency: payload.currency,
            primary: payload.isPrimary,
            owner: userId,
            iban: payload.iban,
            bankCode: accountCode,
            businessId,
            // bankId: bankObj.id,
            routingNumber: payload.routingNum,

            sortCode: payload.sortCode,
            // accountType: payload.accountType
        };

        if (selectedAccount?.id) {
            // @ts-ignore
            addPayload.id = selectedAccount.id;
        }
        try {
            await dispatch(addUpdateBankAccount(addPayload)).unwrap();
            onShowAlert(
                modalType === "add"
                    ? "The new bank account has been added successfully."
                    : "Bank account has been saved successfully."
            );
            await dispatch(merchantBankAccounts(userId))
            onCloseModal();
        } catch (err) {
            onShowErrorAlert(err, err.message);
        }
    };

    const getMerchantAccounts = async () => {
        await dispatch(merchantBankAccounts(userId));
    };

    useEffect(() => {
        if (userId && businessId) {
            getMerchantAccounts();
        }
    }, [userId, businessId]);
    const onShowModal = () => {
        setShowModal(true);
    };
    const onCloseModal = () => {
        setShowModal(false);
    };

    const openAddModal = () => {
        if(hasAddBankPermission || role === "OWNER"){
            setSelectedAccount({});
            setModalType("add");
            onShowModal();
        }
        else{
            onShowErrorAlert("", "Access is denied")
        }
       
    };

    useEffect(() => {
        if (bankAccounts) {
            const tableData = bankAccounts.map((acc) => ({
                key: acc.id,
                id: acc.id,
                bankName: acc.bankName,
                accName: acc.accountName,
                accNum: acc.accountNumber,
                currency: acc.currency,
                accType: acc.accountType,
                country: acc.country,
                iban: acc.iban,
                primary: acc.primary,
                routingNumber: acc.routingNumber,
                sortCode: acc.sortCode,
                swiftCode: acc.swiftCode,
            }));

            const sortedTableData = [...tableData].sort((a,b) => {
                if(a.primary) return -1
                if(b.primary) return 1
                return 0
            })

            setAccounts(sortedTableData);
        }
    }, [bankAccounts]);

    return (
        <Wrapper>
            <Loader isLoading={status === "loading"} />

            {!hasPrimaryAccount && <BusinessStatus
                status_title="No primary account selected"
                status_message={"A primary account must always be selected for settlement."}
                status_icon={<PendingStatusIcon />}
                business_status="pending"
            />}

            <HeaderText>Bank accounts</HeaderText>
            <StyledAccountsTable>
                <ButtonDiv>
                    <button onClick={openAddModal}>
                        <span>Add new bank account</span>
                    </button>
                </ButtonDiv>
                {width > 767 ? (
                    <CustomTable
                        columns={tableColumns}
                        extraEmptyChildren={<EmptyBalanceDescription />}
                        dataSource={accounts}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    />
                ) : (
                    accounts?.map((acc) => {
                        const currentType = acc.primary ? 'primary' : 'secondary'
                        return (
                            <AccountCard
                                key={acc.key}
                                currency={acc.currency}
                                accName={acc.accName}
                                bankName={acc.bankName}
                                accNum={acc.accNum}
                                country={acc.country}
                                account={acc}
                                setSelectedAccount={setSelectedAccount}
                                accountTypeOptions={accountTypeOptions}
                                currentType={currentType}
                                onChange={(value) => handleAccountTypeChange(acc.id, value, currentType)}
                            />
                        )
                       
                    })
                )}
             </StyledAccountsTable>
            {showModal && (
                <CustomModal
                    isModalVisible={showModal}
                    onClose={onCloseModal}
                    width="464px"
                >
                    <AccountModal
                        onSaveAccount={onSaveAccount}
                        modalType={modalType}
                        account={selectedAccount}
                    />
                </CustomModal>
            )}
            {showConfirmAcctTypeChangeModal && pendingChange && 
                <CustomModal
                    isModalVisible={showConfirmAcctTypeChangeModal}
                    onClose={() => setShowConfirmAcctTypeChangeModal(false)}
                    width="464px"
                >
                    <AccountTypeConfirmationModal newAccountType={pendingChange.newType} onCancel={cancelAccountTypeChange} onSubmit={() => confirmAccountTypeChange(pendingChange.accountId)}/>
                </CustomModal>
            }
        </Wrapper>
    );
};

export default Accounts;
